import { secretFetch as fetch, useSecretBroker, secretIdLabel } from "../components/secret-broker/credentials";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Activity, Pause, Play, RefreshCw, Save, ChevronDown } from "lucide-react";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";
const STORAGE_KEY = "secret_dhan_option_decay_controls_v1";
const DATA_CACHE_KEY = "secret_dhan_option_decay_data_v1";
const DHAN_CREDENTIALS_KEY = "secret_dhan_option_decay_credentials_v1";

const C = {
  gold: "#f7c948",
  softGold: "#ffd86b",
  dimGold: "#9f7a24",
  dark: "#020202",
  panel: "#070604",
  card: "#0b0905",
  line: "rgba(247,201,72,0.26)",
  green: "#22c55e",
  emerald: "#006b49",
  red: "#ef4444",
  maroon: "#4a0712",
};

const DECAY_SIDE_COLUMNS = "92px 90px 112px 145px 110px";
const DECAY_SIDE_WIDTH = 549;
const DECAY_GRID_WIDTH = DECAY_SIDE_WIDTH * 2 + 12;

const TABS = [
  { id: "nifty", label: "Nifty Expiry", title: "NIFTY" },
  { id: "sensex", label: "Sensex Expiry", title: "SENSEX" },
  { id: "banknifty", label: "Bank Nifty Expiry", title: "BANKNIFTY" },
];

function fmt(value) {
  return Number(value || 0).toLocaleString("en-IN");
}

function money(value) {
  return Number(value || 0).toFixed(2);
}

function defaultControls() {
  return {
    nifty: { started: false, autoRefresh: false },
    sensex: { started: false, autoRefresh: false },
    banknifty: { started: false, autoRefresh: false },
  };
}

function loadControls() {
  try {
    return { ...defaultControls(), ...JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") };
  } catch {
    return defaultControls();
  }
}

function readDecayCache() {
  try {
    return JSON.parse(localStorage.getItem(DATA_CACHE_KEY) || "{}");
  } catch {
    return {};
  }
}

function getDecaySideValue(row, key) {
  const side = row.decaySide === "PE" ? row.pe : row.ce;

  if (key === "strike") return Number(row.strike || 0);
  if (key === "ltp") return Number(side?.ltp || 0);
  if (key === "oi") return Number(side?.oi || 0);
  if (key === "writerStatus") return String(side?.writerStatus || "");
  if (key === "volume") return Number(side?.volume || 0);
  if (key === "oiChange") return Number(side?.oiChange || 0);
  if (key === "score") return Number(row.score || 0);

  return 0;
}

function SortHeading({ label, sortKey, sortConfig, onSort }) {
  const active = sortConfig.key === sortKey;

  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className="flex items-center gap-1 text-xs font-bold"
      style={{ color: active ? "#22c55e" : "#f7c948" }}
    >
      <span>{label}</span>
      <span style={{ color: active ? "#22c55e" : "#9f7a24" }}>↑↓</span>
    </button>
  );
}

export default function SecretDhanExpiryDecayDesk({
  secretClientId = "",
  secretAccessToken = "",
  secretSpots = {},
}) {
  const [activeTab, setActiveTab] = useState("nifty");
  const [controls, setControls] = useState(loadControls);
  const [dataByTab, setDataByTab] = useState(readDecayCache);
  const [loadingByTab, setLoadingByTab] = useState({});
  const [error, setError] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "score", direction: "desc" });
  const [strikeSearch, setStrikeSearch] = useState("");
  const [credentialForm, setCredentialForm] = useState(() => {
  try {
    const saved = JSON.parse(localStorage.getItem(DHAN_CREDENTIALS_KEY) || "{}");

    return {
      clientId: secretClientId || saved.clientId || "",
      accessToken: secretAccessToken || saved.accessToken || "",
    };
  } catch {
    return {
      clientId: secretClientId || "",
      accessToken: secretAccessToken || "",
    };
  }
});

  const [showCredentials, setShowCredentials] = useState(false);
  const [credentialSaved, setCredentialSaved] = useState("");

  const topScrollRef = useRef(null);
  const dataScrollRef = useRef(null);

  const activeData = dataByTab[activeTab] || {};
  const rows = activeData.rows || [];
  const activeControl = controls[activeTab] || {};

  const syncTopScroll = event => {
    if (dataScrollRef.current) {
        dataScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
    };

  const syncDataScroll = event => {
    if (topScrollRef.current) {
        topScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
    };

    useEffect(() => {
  if (!secretClientId && !secretAccessToken) return;

  setCredentialForm(prev => ({
    clientId: secretClientId || prev.clientId || "",
    accessToken: secretAccessToken || prev.accessToken || "",
  }));

  localStorage.setItem(DHAN_CREDENTIALS_KEY, JSON.stringify({
    clientId: secretClientId || "",
    accessToken: secretAccessToken || "",
  }));
}, [secretClientId, secretAccessToken]);

  const updateControl = (tabId, patch) => {
    setControls(prev => {
      const next = {
        ...prev,
        [tabId]: {
          ...(prev[tabId] || {}),
          ...patch,
        },
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

    const saveDhanCredentials = async () => {
        const cleanCredentials = {
        clientId: String(credentialForm.clientId || "").trim(),
        accessToken: String(credentialForm.accessToken || "").trim(),
        };

        localStorage.setItem(DHAN_CREDENTIALS_KEY, JSON.stringify(cleanCredentials));
        setCredentialForm(cleanCredentials);

        const token = localStorage.getItem("ct_login_token");

        try {
        if (token) {
            await fetch(`${API_BASE}/api/login/admin/secret-settings`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(cleanCredentials),
            });
        }

        setCredentialSaved("Saved");
        setShowCredentials(false);
        setTimeout(() => setCredentialSaved(""), 1800);
        } catch {
        setCredentialSaved("Saved on this page");
        setShowCredentials(false);
        setTimeout(() => setCredentialSaved(""), 1800);
        }
    };

  const loadDecay = async tabId => {
    setError("");
    setLoadingByTab(prev => ({ ...prev, [tabId]: true }));

    try {
        let savedCredentials = {};

        try {
            savedCredentials = JSON.parse(localStorage.getItem(DHAN_CREDENTIALS_KEY) || "{}");
        } catch {
            savedCredentials = {};
        }

        const requestClientId = String(
            credentialForm.clientId || savedCredentials.clientId || secretClientId || ""
        ).trim();

        const requestAccessToken = String(
            credentialForm.accessToken || savedCredentials.accessToken || secretAccessToken || ""
        ).trim();

        const res = await fetch(`${API_BASE}/api/dhan-option-decay/${tabId}?t=${Date.now()}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
            clientId: requestClientId,
            accessToken: requestAccessToken,
            fallbackSpot: secretSpots?.[tabId] || 0,
            }),
    });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(data.error || "Option decay fetch failed");
      }

      setDataByTab(prev => {
        const next = { ...prev, [tabId]: data };
        localStorage.setItem(DATA_CACHE_KEY, JSON.stringify(next));
        window.dispatchEvent(new CustomEvent("option-decay-data", { detail: next }));
        return next;
      });
    } catch (err) {
      setError(err.message || "Option decay fetch failed");
    } finally {
      setLoadingByTab(prev => ({ ...prev, [tabId]: false }));
    }
  };

    const startTab = tabId => {
    updateControl(tabId, { started: true });
    loadDecay(tabId);
    window.dispatchEvent(new Event("option-decay-control"));
  };

  const stopTab = tabId => {
    updateControl(tabId, { started: false, autoRefresh: false });
    window.dispatchEvent(new Event("option-decay-control"));
  };

  const toggleAutoRefresh = tabId => {
    const current = controls[tabId] || {};
    const next = !current.autoRefresh;

    updateControl(tabId, {
      started: next ? true : current.started,
      autoRefresh: next,
    });

    if (next) {
      loadDecay(tabId);
    }

    window.dispatchEvent(new Event("option-decay-control"));
  };

  useEffect(() => {
    const applyCache = event => {
      setDataByTab(event.detail || readDecayCache());
    };

    window.addEventListener("option-decay-data", applyCache);
    setDataByTab(readDecayCache());

    return () => {
      window.removeEventListener("option-decay-data", applyCache);
    };
  }, []);

    useEffect(() => {
        const runStartedTabs = () => {
        TABS.forEach(tab => {
            if (controls[tab.id]?.started) {
            loadDecay(tab.id);
            }
        });
        };

        runStartedTabs();

        const timer = setInterval(runStartedTabs, 30000);

        return () => clearInterval(timer);
    }, [
        controls,
        credentialForm.clientId,
        credentialForm.accessToken,
        secretClientId,
        secretAccessToken,
        secretSpots,
    ]);

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
        const aValue = getDecaySideValue(a, sortConfig.key);
        const bValue = getDecaySideValue(b, sortConfig.key);

        if (typeof aValue === "string" || typeof bValue === "string") {
        return sortConfig.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }

        return sortConfig.direction === "asc"
        ? aValue - bValue
        : bValue - aValue;
    });
    }, [rows, sortConfig]);

  const displayRows = useMemo(() => {
    const q = strikeSearch.trim();

    const filtered = q
      ? sortedRows.filter(row => String(row.strike || "").includes(q))
      : sortedRows;

    return filtered;
  }, [sortedRows, strikeSearch]);

  const sellerComparison = useMemo(() => {
    const totalCallSellers = displayRows.reduce((sum, row) => sum + Number(row.ce?.oi || 0), 0);
    const totalPutSellers = displayRows.reduce((sum, row) => sum + Number(row.pe?.oi || 0), 0);

    const callHigher = totalCallSellers >= totalPutSellers;

    return {
      totalCallSellers,
      totalPutSellers,
      label: callHigher ? "Call Sellers > Put Sellers" : "Put Sellers > Call Sellers",
      left: callHigher ? totalCallSellers : totalPutSellers,
      right: callHigher ? totalPutSellers : totalCallSellers,
    };
  }, [displayRows]);

    const ceDecayRows = useMemo(
    () => displayRows.filter(row => row.decaySide === "CE"),
    [displayRows]
    );

    const peDecayRows = useMemo(
    () => displayRows.filter(row => row.decaySide === "PE"),
    [displayRows]
    );

    const pairedRows = useMemo(() => {
    const total = Math.max(ceDecayRows.length, peDecayRows.length);

    return Array.from({ length: total }, (_, index) => ({
        ce: ceDecayRows[index] || null,
        pe: peDecayRows[index] || null,
    }));
    }, [ceDecayRows, peDecayRows]);

    const toggleSort = key => {
    setSortConfig(prev => {
        if (prev.key === key) {
        return {
            key,
            direction: prev.direction === "desc" ? "asc" : "desc",
        };
        }

        return { key, direction: "desc" };
    });
    };

    const renderDecayHeader = () => (
      <div
        className="grid rounded border text-xs font-bold"
        style={{
          gridTemplateColumns: DECAY_SIDE_COLUMNS,
          width: DECAY_SIDE_WIDTH,
          background: C.panel,
          borderColor: C.line,
        }}
      >
        <div className="px-2 py-2">
          <SortHeading label="CE / PE" sortKey="strike" sortConfig={sortConfig} onSort={toggleSort} />
        </div>

        <div className="px-2 py-2">
          <SortHeading label="LTP" sortKey="ltp" sortConfig={sortConfig} onSort={toggleSort} />
        </div>

        <div className="px-2 py-2">
          <SortHeading label="OI" sortKey="oi" sortConfig={sortConfig} onSort={toggleSort} />
        </div>

        <div className="px-2 py-2">
          <SortHeading label="Writers Status" sortKey="writerStatus" sortConfig={sortConfig} onSort={toggleSort} />
        </div>

        <div className="px-2 py-2">
          <SortHeading label="Volume" sortKey="volume" sortConfig={sortConfig} onSort={toggleSort} />
        </div>
      </div>
    );

    const renderDecayCard = row => {
  if (!row) {
    return (
      <div className="min-h-[118px] rounded border p-3 text-xs" style={{ background: "#020202", borderColor: C.line, color: C.dimGold }}>
        Waiting for candidate
      </div>
    );
  }

  const scoreValue = Number(String(row.score || 0).replace(/[^0-9.]/g, ""));
  const decaySideText = String(row.decaySide || "").trim().toUpperCase();
  const isHighDecay = scoreValue >= 55 && !String(row.result || "").toLowerCase().includes("low priority");
  const blinkCe = row.decaySide === "CE" && isHighDecay;
  const blinkPe = row.decaySide === "PE" && isHighDecay;

  return (
    <div
        className="overflow-hidden rounded border"
        style={{
        width: DECAY_SIDE_WIDTH,
        background: "#020202",
        borderColor: row.decaySide === "CE" ? "rgba(239,68,68,0.75)" : "rgba(168,85,247,0.75)",
        }}
    >
        <div
        className="grid text-xs"
        style={{ gridTemplateColumns: DECAY_SIDE_COLUMNS }}
        >
        <div className="px-2 py-2 font-bold leading-5 whitespace-nowrap">
            <div>CE {row.strike}</div>
            <div>PE {row.strike}</div>
        </div>

        <div className="px-2 py-2 font-bold leading-5 whitespace-nowrap">
            <div
                className={decaySideText === "CE" && isHighDecay ? "decay-price-blink" : ""}
                style={{ color: decaySideText === "CE" ? C.green : C.gold }}
            >
                Rs.{money(row.ce?.ltp)}
            </div>

            <div
                className={decaySideText === "PE" && isHighDecay ? "decay-price-blink" : ""}
                style={{ color: decaySideText === "PE" ? C.green : C.gold }}
            >
                Rs.{money(row.pe?.ltp)}
            </div>
        </div>

        <div className="px-2 py-2 font-bold leading-5 whitespace-nowrap">
          <div>{fmt(row.ce?.oi)}</div>
          <div className="text-[10px] leading-4" style={{ color: row.ce?.oiChange >= 0 ? C.green : C.red }}>
            Chg {row.ce?.oiChange >= 0 ? "+" : ""}{fmt(row.ce?.oiChange)}
          </div>

          <div className="mt-1">{fmt(row.pe?.oi)}</div>
          <div className="text-[10px] leading-4" style={{ color: row.pe?.oiChange >= 0 ? C.green : C.red }}>
            Chg {row.pe?.oiChange >= 0 ? "+" : ""}{fmt(row.pe?.oiChange)}
          </div>
        </div>

        <div className="px-2 py-2 leading-5 whitespace-nowrap">
          <div style={{ color: row.ce?.writerStatus === "Short Buildup" ? C.green : C.gold }}>
            {row.ce?.writerStatus || "--"}
          </div>
          <div style={{ color: row.pe?.writerStatus === "Short Buildup" ? C.green : C.gold }}>
            {row.pe?.writerStatus || "--"}
          </div>
        </div>

        <div className="px-2 py-2 font-bold leading-5 whitespace-nowrap">
          <div>{fmt(row.ce?.volume)}</div>
          <div>{fmt(row.pe?.volume)}</div>
        </div>
      </div>

      <div className="border-t px-2 py-2 text-xs" style={{ borderColor: C.line }}>
        <div className="font-bold" style={{ color: C.green }}>
            {row.decaySide} {row.result} ({row.score}/100)
        </div>

        </div>
    </div>
  );
};

  useSecretBroker(({ clientId, accessToken }) => {
    setCredentialForm(current => ({ ...current, clientId, accessToken }));
  });

  return (
    <div className="min-h-screen p-5" style={{ background: C.dark, color: C.gold }}>
        <style>
            {`
                @keyframes decayPriceBlink {
                    0%, 100% {
                        background: rgba(0, 107, 73, 0.25);
                        color: #f7c948;
                        box-shadow: 0 0 4px rgba(34,197,94,0.25);
                    }

                    50% {
                        background: #006b49;
                        color: #ffffff;
                        box-shadow: 0 0 18px rgba(34,197,94,0.95);
                    }
                }

                    .decay-price-blink {
                    display: inline-block !important;
                    min-width: 76px;
                    border-radius: 6px;
                    padding: 2px 6px;
                    animation: decayPriceBlink 0.75s ease-in-out infinite !important;
                }
                .decay-reason-two-line {
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
                }
            `}
        </style>
        <div className="flex flex-wrap items-start justify-between gap-4 border-b pb-4" style={{ borderColor: C.line }}>
        <div className="max-w-[900px]">
          <div className="text-[10px] font-bold uppercase tracking-[0.28em]" style={{ color: C.dimGold }}>
            NEXOVATE OPTION EXPIRY TERMINAL
          </div>

          <h1 className="mt-1 text-2xl font-bold" style={{ color: C.softGold }}>
            Dhan Expiry Decay Desk
          </h1>

          <div className="mt-1 text-xs">
            Dhan option-chain source, Secret Page settings, CE/PE writer comparison.
          </div>
        </div>

        <div className="text-right text-[11px] font-bold uppercase tracking-[0.18em] leading-6">
          <div>Spot: {fmt(activeData.spot)}</div>
          <div>Expiry: {activeData.expiry || "--"}</div>
          <div>Rows: {rows.length}</div>
          <div>{activeData.updatedAt ? new Date(activeData.updatedAt).toLocaleTimeString("en-IN") : "--"}</div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-start gap-2">
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`rounded px-5 py-3 font-bold ${activeTab === tab.id ? "option-decay-green" : ""}`}
            style={{
              background: activeTab === tab.id ? "linear-gradient(180deg, #00875f, #006b49)" : C.card,
              border: `1px solid ${activeTab === tab.id ? C.green : C.line}`,
              color: activeTab === tab.id ? "#ffffff" : C.gold,
              boxShadow: activeTab === tab.id ? "0 0 18px rgba(0,107,73,0.85)" : "none",
            }}
          >
            {tab.label}
          </button>
        ))}

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowCredentials(prev => !prev)}
            className="rounded px-4 py-3 text-xs font-bold"
            style={{
              background: C.card,
              border: `1px solid ${C.line}`,
              color: C.gold,
            }}
          >
            Credentials <ChevronDown size={13} className="inline ml-1" />
          </button>

          {showCredentials && (
            <div
              className="absolute left-0 top-full z-20 mt-2 w-[560px] rounded border p-3"
              style={{ background: "#020202", borderColor: C.line }}
            >
              <div className="grid grid-cols-[1fr_150px_auto] gap-2 items-end text-[10px]">
                <div>
                  <label className="mb-1 block font-bold uppercase tracking-[0.22em]" style={{ color: C.dimGold }}>
                    Access Token
                  </label>
                  <input
                    value={credentialForm.accessToken}
                    onChange={e => setCredentialForm(prev => ({ ...prev, accessToken: e.target.value }))}
                    className="w-full rounded border px-2 py-2 text-xs font-bold outline-none"
                    style={{ background: "#020202", borderColor: C.line, color: C.gold }}
                  />
                </div>

                <div>
                  <label className="mb-1 block font-bold uppercase tracking-[0.22em]" style={{ color: C.dimGold }}>
                    {secretIdLabel()}
                  </label>
                  <input
                    value={credentialForm.clientId}
                    onChange={e => setCredentialForm(prev => ({ ...prev, clientId: e.target.value }))}
                    className="w-full rounded border px-2 py-2 text-xs font-bold outline-none"
                    style={{ background: "#020202", borderColor: C.line, color: C.gold }}
                  />
                </div>

                <button
                  type="button"
                  onClick={saveDhanCredentials}
                  className="option-decay-green flex items-center gap-2 rounded px-3 py-2 text-xs font-bold"
                  style={{
                    background: "linear-gradient(180deg, #006b49, #003f2f)",
                    border: `1px solid ${C.green}`,
                    color: "#ffffff",
                  }}
                >
                  <Save size={13} /> Save
                </button>
              </div>

              {credentialSaved && (
                <div className="mt-2 text-xs font-bold" style={{ color: C.green }}>
                  {credentialSaved}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 rounded border p-4" style={{ background: C.panel, borderColor: C.line }}>
        <button
          type="button"
          onClick={() => startTab(activeTab)}
          className="option-decay-green flex items-center gap-2 rounded px-5 py-3 font-bold"
          style={{
            background: "linear-gradient(180deg, #006b49, #003f2f)",
            border: `1px solid ${C.green}`,
            color: "#ffffff",
            boxShadow: "0 0 16px rgba(0,107,73,0.65)",
          }}
        >
          <Play size={18} /> Start {TABS.find(t => t.id === activeTab)?.title}
        </button>

        <button
          type="button"
          onClick={() => stopTab(activeTab)}
          className="option-decay-maroon flex items-center gap-2 rounded px-5 py-3 font-bold"
          style={{
            background: "linear-gradient(180deg, #4a0712, #1f0308)",
            border: "1px solid rgba(239,68,68,0.5)",
            color: "#fecaca",
            boxShadow: "0 0 14px rgba(239,68,68,0.35)",
          }}
        >
          <Pause size={18} /> Stop
        </button>

        <button
          type="button"
          onClick={() => toggleAutoRefresh(activeTab)}
          className={`flex items-center gap-2 rounded px-5 py-3 font-bold ${activeControl.autoRefresh ? "option-decay-green" : ""}`}
          style={{
            background: activeControl.autoRefresh ? "linear-gradient(180deg, #006b49, #003f2f)" : C.card,
            border: `1px solid ${activeControl.autoRefresh ? C.green : C.line}`,
            color: activeControl.autoRefresh ? "#ffffff" : C.gold,
            boxShadow: activeControl.autoRefresh ? "0 0 16px rgba(0,107,73,0.65)" : "none",
          }}
        >
          <RefreshCw size={18} /> Auto Refresh 30s
        </button>

        <button
          type="button"
          onClick={() => loadDecay(activeTab)}
          className="option-decay-green flex items-center gap-2 rounded px-5 py-3 font-bold"
          style={{
            background: "linear-gradient(180deg, #006b49, #003f2f)",
            border: `1px solid ${C.green}`,
            color: "#ffffff",
            boxShadow: "0 0 16px rgba(0,107,73,0.65)",
          }}
        >
          <Activity size={18} /> Fetch Now
        </button>

        {loadingByTab[activeTab] && <div style={{ color: C.dimGold }}>Loading...</div>}
        {activeControl.started && <div style={{ color: C.green }}>Running</div>}

        <div
          className="text-sm font-bold"
          style={{ color: sellerComparison.totalCallSellers >= sellerComparison.totalPutSellers ? C.red : C.green }}
        >
          {sellerComparison.label}
          <span className="ml-2" style={{ color: C.gold }}>
            ({fmt(sellerComparison.left)}) &gt; ({fmt(sellerComparison.right)})
          </span>
        </div>

        <input
          value={strikeSearch}
          onChange={e => setStrikeSearch(e.target.value)}
          placeholder="Search strike"
          className="ml-auto rounded border px-4 py-3 font-bold outline-none"
          style={{
            width: 190,
            background: "#020202",
            borderColor: C.line,
            color: C.gold,
          }}
        />
      </div>

      {error && (
        <div className="mt-4 rounded border p-3" style={{ borderColor: C.red, color: C.red, background: "#190407" }}>
          {error}
        </div>
      )}

      <div className="mt-5 rounded border p-3" style={{ borderColor: C.line }}>
        <div
            ref={topScrollRef}
            onScroll={syncTopScroll}
            className="mb-3 overflow-x-auto"
        >
            <div style={{ width: DECAY_GRID_WIDTH, height: 12 }} />
        </div>

        <div
            ref={dataScrollRef}
            onScroll={syncDataScroll}
            className="overflow-x-auto"
        >
            <div style={{ width: DECAY_GRID_WIDTH }}>
            <div className="grid gap-3" style={{ gridTemplateColumns: `${DECAY_SIDE_WIDTH}px ${DECAY_SIDE_WIDTH}px` }}>
                {renderDecayHeader()}
                {renderDecayHeader()}
            </div>

            <div className="mt-3 space-y-3">
                {pairedRows.map((pair, index) => (
                <div
                    key={`pair-${index}-${pair.ce?.strike || "ce"}-${pair.pe?.strike || "pe"}`}
                    className="grid gap-3"
                    style={{ gridTemplateColumns: `${DECAY_SIDE_WIDTH}px ${DECAY_SIDE_WIDTH}px` }}
                >
                    {renderDecayCard(pair.ce)}
                    {renderDecayCard(pair.pe)}
                </div>
                ))}
            </div>

            {!pairedRows.length && (
                <div className="p-10 text-center" style={{ color: C.dimGold }}>
                Start this tab or click Fetch Now to scan expiry decay.
                </div>
            )}
            </div>
        </div>
        </div>
    </div>
  );
}